import {
  Component,
  Input,
  EventEmitter,
  Output,
  OnInit,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  Renderer2,
  OnDestroy,
  ViewEncapsulation,
  ContentChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

import { SelectDropdownItemComponent } from './select-dropdown-item/select-dropdown-item.component';
import { IdentificationSelectionPriority } from './domain/identification-selection-priority';
import { IdentificationPriority } from './domain/identification-priority';
import { SelectionPriority } from './domain/selection-priority';
import { Unsorted } from './domain/unsorted';
import { Sort } from './interfaces/sort';
import { SelectDropdownIconDirective } from './select-dropdown-icon-directive/select-dropdown-icon.directive';
import { SelectDropdownActionDirective } from './select-dropdown-action-directive/select-dropdown-action.directive';
import { Boundaries } from '@purespectrum1/ui/shared';

type DropdownLabelAlignment = 'start' | 'center' | 'end';

@Component({
  selector: 'ps-ui-select-dropdown',
  templateUrl: './select-dropdown.component.html',
  styleUrls: ['./select-dropdown.component.scss'],
  exportAs: 'psUiSelectDropdown',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SelectDropdownComponent,
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.Emulated, // Use to disable CSS Encapsulation for this component
})
export class SelectDropdownComponent<T extends { [key: string]: any }>
  implements OnInit, ControlValueAccessor, OnDestroy, OnChanges
{
  private _dropdownItems: T[] = [];
  @Input()
  set dropdownItems(value: T[]) {
    this._dropdownItems = value;
    this._init();
  }
  get dropdownItems(): T[] {
    return this._dropdownItems;
  }

  @Input() set searchText(text: string | undefined) {
    if (text) {
      this.searchTextControl.setValue(text);
    }
  }

  @Input() idField: string = 'id';
  @Input() field: string = 'text';
  @Input() searchByKeys: string[] = [];
  @Input() label?: string;
  @Input() labelNone: string = 'Select';
  @Input() labelAlignment: DropdownLabelAlignment = 'center';
  @Input() spanField?: string;
  @Input() enableSearch: boolean = true;
  @Input() clearSearch: boolean = true;
  @Input() multi: boolean = false;
  @Input() allSelected: boolean = false;
  @Input() firstSelected: boolean = false;
  @Input() loading: boolean = true;
  @Input() disabled: boolean = false;
  @Input() icon: boolean = false;
  @Input() iconClass: string = 'fa fa-folder';
  @Input() newTextInput: boolean = false;
  @Input() showNewTextField: boolean = true;
  @Input() newTextSelectionType: string = 'label';
  @Input() newTextInputPlaceHolder: string = 'New List';
  @Input() newInputLabel: string = 'New List';
  @Input() noItemMessage: string = '';
  @Input() sortBySelected: boolean = false;
  @Input() orderById: boolean = false;
  @Input() orderBy: string = '';
  @Input() mcIconIdentifier: string = '';
  @Input() addNewItem: boolean = false;
  @Input() addNewItemDropDown: T[] = [];
  @Input() indicator = false;
  @Input() searchDropdownItems = true;
  @Input() showClearStateButton: boolean = false;
  @Output() readonly clearState = new EventEmitter();
  @Input() boundaries?: DOMRect;
  @Input() maxHeight: number = 250;
  @Input() eventListeningContext?: string = 'document';
  @Input() groups: Record<number, string> = {};

  @Input()
  set searchTerm(term: string) {
    this.searchTextControl.patchValue(term);
  }

  @Output() readonly selectedItem = new EventEmitter();
  @Output() readonly selectedItems = new EventEmitter();
  @Output() readonly selectedFooterItem = new EventEmitter();
  @Output() readonly submitButtonClicked = new EventEmitter();
  @Output() readonly search = new EventEmitter<string>();
  @ViewChild('toggleButton') toggleButton!: ElementRef;
  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChildren(SelectDropdownItemComponent)
  items!: QueryList<SelectDropdownItemComponent>;

  // Virtual scrolling properties
  @ViewChild('searchContainer')
  private _searchContainer!: ElementRef;
  @ViewChild('multiSelectContainer')
  private _multiSelectContainer!: ElementRef;
  @ViewChild('scrollViewport')
  private _scrollViewport: CdkVirtualScrollViewport;
  @ViewChild('newFooterInputContainer')
  private _newFooterInputContainer!: ElementRef;
  @ViewChild('noItemMsgContainer')
  private _noItemMsgContainer!: ElementRef;
  @ViewChild('templateNameInput') set templateNameInput(content: ElementRef) {
    if (content) {
      // initially setter gets called with undefined
      content.nativeElement.focus();
    }
  }

  @ContentChild(SelectDropdownIconDirective, { static: true })
  selectIcon?: SelectDropdownIconDirective;

  @ContentChild(SelectDropdownActionDirective, { static: true })
  action?: SelectDropdownActionDirective;

  public scrollViewportHeight: number;
  // Setting this to the default dropdown item height in Chrome
  public itemHeight = 29;

  isOpen: boolean = false;
  searchTextControl = new FormControl('');
  isButtonClicked: boolean = false;
  newText: boolean = false;
  inputItem = new FormControl('');
  dropup = false;

  private _outsideClickListener?: () => void;

  constructor(private _renderer: Renderer2, private _elementRef: ElementRef) {}

  get textAlignment() {
    return `text-${this.labelAlignment}`;
  }

  get background() {
    if (!this.isOpen) {
      return '';
    }

    return 'dropdown-button-open';
  }

  get dropdownToggle() {
    if (this.selectIcon) {
      return '';
    }

    return 'dropdown-toggle';
  }

  private _selected: T[] = [];
  get selected(): T[] {
    return this._selected;
  }
  set selected(item: T[]) {
    this._selected = item;
    if (this.multi) {
      this.selectedItems.emit(this._selected);
      this.onChange(this._selected);
    } else {
      this.selectedItem.emit(this._selected[0]);
      this.onChange(this._selected[0]);
    }
  }
  public addNoMatchItem(items: T[]) {
    this.selected = items;
  }
  get selectedItemsCount(): number {
    return this.multi ? this.selected.length : -1;
  }

  onChange: (item?: T | T[]) => void = () => void 0;

  ngOnInit(): void {
    this._init();
    if (this.eventListeningContext) {
      this._listenToOutsideClicks();
    }
    this._resizeDropdownOnSearch();
  }

  ngOnDestroy(): void {
    if (this._outsideClickListener) {
      this._outsideClickListener();
    }
  }

  private _init() {
    if (!(this.dropdownItems instanceof Array)) {
      return;
    }

    // automatic remove loading
    if (this.loading && this.dropdownItems.length) {
      this.loading = false;
    }

    // start with first item selected
    if (this.firstSelected && this.dropdownItems.length) {
      const first = this.dropdownItems[0];
      this.selected = [first];
      return;
    }

    if (this.multi) {
      if (this.allSelected) {
        // start with all items selected
        this.selectAll();
      }
    }

    this.searchByKeys = [...this.searchByKeys, this.field];

    // prevent change detection after checked errors with height resizing
    setTimeout(() => {
      this._resizeDropdownViewport();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.eventListeningContext &&
      this.eventListeningContext &&
      this.eventListeningContext !== 'document'
    ) {
      this._listenToOutsideClicks();
    }
  }

  private _listenToOutsideClicks() {
    let element: any = this.eventListeningContext;

    if (
      this.eventListeningContext &&
      this.eventListeningContext !== 'document'
    ) {
      element =
        document.querySelector(this.eventListeningContext)?.parentNode ||
        'document';
    }

    this._outsideClickListener = this._renderer.listen(
      element,
      'click',
      (event: Event) => {
        const attrId = (event.target as HTMLElement).id;
        if (
          !this._elementRef.nativeElement.contains(event.target) &&
          ![
            'footer-button-check',
            'footer-button',
            'new-input-remove',
            'new-input-list',
            'new-input-icon',
            'new-input-icon-span',
          ].includes(attrId)
        ) {
          {
            this.close();
          }
        }
      }
    );
  }

  private _resizeDropdownOnSearch() {
    this.searchTextControl.valueChanges
      .pipe(debounceTime(100))
      .subscribe((searchValue) => {
        // Allow the dropdown to rerender the new items the resize it
        setTimeout(() => {
          this.search.emit(searchValue);
          this._resizeDropdownViewport();
        }, 0);
      });
  }

  startClick() {
    this.isButtonClicked = true;
  }
  endClick() {
    this.isButtonClicked = false;
  }

  updateState() {
    this.selected = [];
    this.searchTextControl.patchValue('');
    this.clearState.emit();
  }

  toggle() {
    this.isOpen = !this.isOpen;
    // focus on searchBox when dropdown is opened
    if (this.isOpen) {
      this._focusSearchInput();
      this.dropdownItems = this._sortable().sort();
    } else {
      this.toggleButton.nativeElement.focus();
    }

    this._direction();
  }

  private _direction(): void {
    if (this.boundaries) {
      const elRect =
        this._scrollViewport.elementRef.nativeElement.getBoundingClientRect();

      this.dropup = new Boundaries(
        this.boundaries,
        elRect,
        'vertical'
      ).outside();
    }
  }

  open() {
    this._direction();
    this.isOpen = true;
    // focus on searchBox when dropdown is opened
    this._focusSearchInput();
  }

  close() {
    this.isOpen = false;
    this.dropup = false;
  }

  closeFocus() {
    this.isOpen = false;
    this.toggleButton.nativeElement.focus();
  }

  writeValue(item: T | T[]): void {
    if (item && !Array.isArray(item)) {
      item = [item];
    }
    this._selected = item || [];
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    // do nothing. Needed for ControlValueAccessor interface
  }
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  scrollUp() {
    this._scrollViewport.scrollTo({ top: 0 });
  }

  focusItems(e: KeyboardEvent, isToggleButton?: boolean, upClose?: boolean) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();

      if (isToggleButton && !this.isOpen) {
        // when arrow down is pressed in closed dropdown
        // open it
        this.open();
      } else if (this.items.first) {
        // focus first item
        this.items.first.focus();
      }
    } else if (e.key === 'Escape') {
      this.closeFocus();
    } else if (e.key === 'ArrowUp') {
      if (isToggleButton || upClose) {
        // close dropdown when press up and toggle button is focused
        this.closeFocus();
        return;
      }
      const activeEl = document.activeElement;
      if (
        this.searchInput.nativeElement === activeEl ||
        this.items.first.item.nativeElement === activeEl
      ) {
        // focus toggleButton if first element is focused
        this.toggleButton.nativeElement.focus();
      }
    }
  }

  focusNext(i: number) {
    const nextItem = this.items.get(i + 1);
    if (nextItem) {
      nextItem.focus();
    }
  }

  focusPrevious(i: number) {
    const previousItem = this.items.get(i - 1);
    if (previousItem) {
      previousItem.focus();
    } else if (this.enableSearch) {
      this.searchInput.nativeElement.focus();
    } else {
      this.closeFocus();
    }
  }

  findSelectedItemIndex(item: T): number {
    return this.selected.findIndex(
      (selectedItem) =>
        this._getIdFieldValue(selectedItem) === this._getIdFieldValue(item)
    );
  }

  private _focusSearchInput() {
    let el: ElementRef | null = null;
    if (this.enableSearch) {
      el = this.searchInput;
    } else if (this.action) {
      el = null;
    } else if (this.items.first) {
      // focus first item if search isn't enabled
      el = this.items.first.item;
    }
    if (el) {
      // need to wait the dropdown opening to render
      // before focus the element
      setTimeout(() => {
        el?.nativeElement.focus();
      }, 0);
    }
  }

  private _sortable(): Sort<T> {
    if (this.orderBy && this.sortBySelected) {
      return new IdentificationSelectionPriority(
        this._dropdownItems,
        this.selected,
        this.orderBy
      );
    }

    if (this.orderBy) {
      return new IdentificationPriority(this._dropdownItems, this.orderBy);
    }

    if (this.sortBySelected) {
      return new SelectionPriority(
        this._dropdownItems,
        this.selected,
        this.idField
      );
    }

    return new Unsorted(this._dropdownItems);
  }

  private _getIdFieldValue(item: T) {
    return item[this.idField];
  }

  get selectedItemLabel(): string {
    return this.selected[0]?.[this.field] || '';
  }

  get allItemsSelected() {
    return this.selectedItemsCount === this.dropdownItems.length;
  }

  getLabel() {
    if (this.loading) {
      return 'Loading...';
    }

    if (this.label) {
      return this.label;
    }

    if (!this.dropdownItems.length) {
      return this.labelNone;
    }

    if (this.multi) {
      if (this.allItemsSelected) {
        return 'All';
      }
      if (!this.selectedItemsCount) {
        return this.labelNone;
      }
      if (this.selectedItemsCount === 1) {
        return this.selectedItemLabel;
      }
      return this.selectedItemsCount + ' selected';
    }

    // single selection dropdown
    return this.selectedItemLabel || this.labelNone;
  }

  indicatorCounter(): number {
    const counter = this.selected?.length;
    if (this.searchInput?.nativeElement.value) {
      return counter + 1;
    }

    return counter;
  }

  emptySearch() {
    this.searchTextControl.setValue('');
    this._focusSearchInput();
  }

  selectAll() {
    if (!this.dropdownItems.length) {
      return;
    }

    this.selected = [...this.dropdownItems];
    this._focusSearchInput();
  }

  selectNone() {
    if (!this.dropdownItems.length) {
      return;
    }

    this.selected = [];
    this._focusSearchInput();
  }

  selectItem(item: T) {
    if (this.multi) {
      return this._updateMultiSelect(item);
    }
    this.selected = [item];
    this.isOpen = false;
  }

  private _updateMultiSelect(item: any) {
    const idx = this.findSelectedItemIndex(item);
    if (idx > -1) {
      this.selected = [
        ...this.selected.slice(0, idx),
        ...this.selected.slice(idx + 1),
      ];
    } else {
      this.selected = [...this.selected, item];
    }
  }

  public changeTextBox() {
    this.newText = this.showNewTextField && !this.newText;
    if (!this.showNewTextField) {
      this.submitButtonClicked.emit();
      this.close();
    }
  }

  public saveFooterItem() {
    if (this.inputItem.value) {
      this.selectedFooterItem.emit(this.inputItem.value);
      this.newText = false;
      this.inputItem.setValue('');
      this.isOpen = false;
    }
  }

  public removeEmptySpace() {
    const regx = /^\s+$/;
    if (regx.test(this.inputItem.value)) {
      return this.inputItem.setValue(this.inputItem.value.replace(regx, ''));
    }
  }

  private _resizeDropdownViewport() {
    // Ensure we have to correct height of the dropdown items
    if (this.items.length) {
      this.itemHeight = this.items.first.item.nativeElement.offsetHeight;
    }

    // We get double scrollbars when the item viewport is larger than dropdown max height
    const boundaries = this.boundaries
      ? this.boundaries.height / 2 - 20
      : this.maxHeight;
    const MAX_HEIGHT =
      boundaries < this.maxHeight ? boundaries : this.maxHeight;

    const itemLength = this.items.length;
    let totalScrollViewportHeight = itemLength * this.itemHeight;
    if (this.enableSearch) {
      totalScrollViewportHeight +=
        this._searchContainer.nativeElement.offsetHeight;
    }
    if (this.multi && !this.action) {
      totalScrollViewportHeight +=
        this._multiSelectContainer.nativeElement.offsetHeight;
    }
    if (this.action) {
      totalScrollViewportHeight += 100;
    }
    if (this.newTextInput) {
      totalScrollViewportHeight +=
        this._newFooterInputContainer.nativeElement.offsetHeight;
    }
    if (this.addNewItem) {
      totalScrollViewportHeight +=
        this._newFooterInputContainer.nativeElement.offsetHeight;
    }
    if (this.noItemMessage && !this.dropdownItems.length) {
      totalScrollViewportHeight +=
        this._noItemMsgContainer.nativeElement.offsetHeight;
    }

    // make room for the border
    totalScrollViewportHeight += 2;

    const heightDiff = totalScrollViewportHeight - this.scrollViewportHeight;

    // Ignore updating the container height if it won't change anything
    if (
      heightDiff === 0 ||
      (totalScrollViewportHeight > MAX_HEIGHT &&
        this.scrollViewportHeight === MAX_HEIGHT)
    ) {
      return;
    }

    // Use setTimeout to ignore change detection after check errors
    setTimeout(() => {
      if (totalScrollViewportHeight > MAX_HEIGHT) {
        this.scrollViewportHeight = MAX_HEIGHT;
      } else {
        this.scrollViewportHeight = totalScrollViewportHeight;
      }
    }, 0);
    // Give the container a moment to repaint before updating the virtual scroll viewport
    setTimeout(() => {
      this._scrollViewport.checkViewportSize();
    }, 50);
  }
}
